import type { EntityState } from '@reduxjs/toolkit';
import type { QcsResourceSlice } from 'app-components/flux/core/resourceSliceUtils';

type BillerStatusType = 'SUBMITTED' | 'PENDING' | 'CONNECTED' | 'FAILED' | 'DISCONNECTED';

type PrintSuppressionType = 'Mandatory' | 'Choice' | 'Notice';

// ### - -       Search types        - - ###
// ### - - - - - - - - - - - - - - - - - ###
export type BillerAddress = {
  id?: string;
  line1?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
};

export interface Biller {
  id: string;
  name: string;
  mode?: string;
  network?: string;
  status?: string;
  url?: string;
  logoKey?: string;
  classification?: string;
  activation?: string;
  purpose?: string;
  addresses?: BillerAddress[];
  externalId?: string;
}

export type SearchResponse = {
  data: Biller[];
  metaData?: any;
};

// ### -      Enrollment (parameter) types       - ###
// ### - - - - - - - - - - - - - - - - - - - - - - ###
export interface ErrorInfo {
  code: string;
  message: string;
}
export interface Consent {
  content?: string;
  // externalId?: string;
  error?: ErrorInfo;
  value?: string;
}
export interface Validation {
  required?: boolean;
  regex?: string;
  minLength?: number;
  maxLength?: number;
}
export interface Field {
  name: string;
  label: string;
  helpText?: string;
  sequence?: number;
  externalId?: string;
  error?: ErrorInfo;
  validation?: Validation;
}
export interface PrintSuppression {
  error?: ErrorInfo;
  type: PrintSuppressionType;
  value: string;
}
export interface Parameter {
  name: string;
  label: string;
  fields: Field[];
  sequence?: number;
  externalId?: string;
  error?: ErrorInfo;
}

export interface EnrollData {
  biller: Biller;
  consent: Consent;
  printSuppression: PrintSuppression;
  parameters: Parameter[];
  errors?: ErrorInfo[];
}

export interface EnrollResponse {
  biller: Biller;
  consent: Consent;
  printSuppression: PrintSuppression;
  parameters: Parameter[];
}

// ### -    Enrollment validation types    - ###
// ### - - - - - - - - - - - - - - - - - - - ###

export interface ValidateRequest {
  // TODO: update with values
  consent: Consent;
  printSuppression: PrintSuppression;
  parameters: Parameter[];
}

export interface ValidateResponse extends EnrollResponse {
  errors: ErrorInfo[];
}

// ### -         Submission types          - ###
// ### - - - - - - - - - - - - - - - - - - - ###

export interface SubmitField {
  name: string;
  value: string;
}
export interface SubmitRequest {
  nickName?: string;
  reference?: string;
  billerId: string;
  data: {
    consent: {
      value: string;
    };
    printSuppression: {
      value: string;
    };
    parameters: {
      name: string;
      fields: SubmitField[];
    }[];
  };
}

// ### -      Biller Account types         - ###
// ### - - - - - - - - - - - - - - - - - - - ###
interface Detail {
  externalCode: string;
  code: string;
  errorCode: string;
  description: string;
}
interface AdditionalInfo {
  code: string;
  description: string;
  details: Detail[];
}

export interface BillerAccount {
  id: string;
  billerId: string;
  status: BillerStatusType;

  nickname?: string;
  reference?: string;
  bill?: string;
  additonalInfo?: AdditionalInfo;
  data?: EnrollData;
}

export type BillPresentmentSlice = Omit<QcsResourceSlice, 'ids' | 'entities'> & {
  billers: EntityState<Biller, string>;
  billerAccounts: EntityState<BillerAccount, string>;
};

export const BILL_PRESENTMENT_REDUCER_KEY = 'billPresentmentSlice';
export type BILL_PRESENTMENT_REDUCER_KEY = typeof BILL_PRESENTMENT_REDUCER_KEY;

// declare module 'app-components/utils/redux-store/rootState' {
//   export default interface RootState {
//     [BILL_PRESENTMENT_REDUCER_KEY]: BillPresentmentSlice;
//   }
// }
